import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, Subscription } from 'rxjs';
import { LaboursService } from '../../services/labours.service';
import { AdvanceLedger, AdvanceLedgerEntry, ProductionTab } from '../../production-inventory.model';
import { decimalSign, formatDecimalAmount } from '../../../utils';

@Component({
  selector: 'app-labour-advance-ledger',
  standalone: false,
  templateUrl: './labour-advance-ledger.component.html',
})
export class LabourAdvanceLedgerComponent implements OnInit, OnDestroy {
  protected labourId!: string;
  protected labourName: string = '';
  protected ledger: AdvanceLedger | null = null;
  protected isLoading: boolean = true;
  protected errorMessage: string | null = null;

  protected filters: { from_date: string; to_date: string } = { from_date: '', to_date: '' };

  private subscription: Subscription = new Subscription();

  protected readonly formatDecimalAmount = formatDecimalAmount;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private laboursService: LaboursService
  ) {}

  ngOnInit(): void {
    this.labourId = this.route.snapshot.paramMap.get('id')!;
    this.labourName = (history.state as { name?: string })?.name ?? '';

    this.loadLedger();
  }

  protected loadLedger(): void {
    this.isLoading = true;
    this.errorMessage = null;

    const sub = this.laboursService
      .advance_ledger(this.labourId, this.filters)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (ledger: AdvanceLedger) => {
          this.ledger = ledger;
          this.labourName = ledger.full_name || this.labourName;
        },
        error: (error: any) => {
          this.ledger = null;
          // A malformed date comes back as 400 with a `detail` explaining which one.
          this.errorMessage = error?.error?.detail ?? 'Could not load the advance ledger. Please try again.';
        },
      });

    this.subscription.add(sub);
  }

  protected onApplyFilters(): void {
    if (this.dateRangeError) return;

    this.loadLedger();
  }

  protected onClearFilters(): void {
    this.filters = { from_date: '', to_date: '' };
    this.loadLedger();
  }

  protected get dateRangeError(): string | null {
    const { from_date, to_date } = this.filters;
    if (from_date && to_date && from_date > to_date) return '"From" date must be on or before the "To" date.';

    return null;
  }

  protected get hasDateFilter(): boolean {
    return !!(this.filters.from_date || this.filters.to_date);
  }

  /** The opening balance only needs surfacing when it actually carries a value forward. */
  protected get showOpeningBalance(): boolean {
    return decimalSign(this.ledger?.opening_balance) !== 0;
  }

  /** Payroll-generated rows are read-only history — they must not look editable. */
  protected isSalaryDeduction(entry: AdvanceLedgerEntry): boolean {
    return entry.entry_type === 'salary_deduction';
  }

  protected entryTypeLabel(entry: AdvanceLedgerEntry): string {
    switch (entry.entry_type) {
      case 'advance_given':
        return 'Advance given';
      case 'advance_returned':
        return 'Returned by labour';
      case 'salary_deduction':
        return 'Salary deduction';
      default:
        return entry.entry_type;
    }
  }

  protected goBack(): void {
    // The overview reads its tab off `?tab=`, and defaults to Production Entries without
    // it. The ledger is only reachable from Labours, so name that tab on the way back.
    void this.router.navigate(['/production-inventory'], {
      queryParams: { tab: ProductionTab.LABOURS },
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
